/**
 * Upload type and size limits for the three Storage buckets (#47).
 *
 * The SERVER enforces these: migration
 * `20261016130000_storage_bucket_upload_limits.sql` sets `file_size_limit` and
 * `allowed_mime_types` on each bucket, and Storage refuses a non-conforming
 * upload no matter how the request was made. The checks in this file are the
 * client mirror — they exist so a person gets a clear message before a
 * round trip, not as the boundary. `storageUploadLimits.test.ts` fails if the
 * migration and these constants drift apart.
 *
 * Raster images only. SVG is deliberately absent: these buckets are public and
 * an SVG can carry script, which is the stored-XSS path the issue describes.
 */

const MB = 1024 * 1024;

/** Image types every bucket accepts. `image/jpg` is non-standard but is what
 * the import-org-content Edge Function can pass through from a remote server. */
export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
] as const;

export const PDF_MIME_TYPE = "application/pdf";

/** Client cap for one image, in every bucket. */
export const MAX_IMAGE_BYTES = 5 * MB;
/** Client cap for one PDF (feed "download" posts, organization-content only). */
export const MAX_PDF_BYTES = 10 * MB;

export type StorageBucketId = "avatars" | "gallery-photos" | "organization-content";

export interface BucketUploadLimits {
  /** Bytes. Storage applies one ceiling per bucket. */
  fileSizeLimit: number;
  allowedMimeTypes: readonly string[];
}

/**
 * The server-side configuration, bucket by bucket. organization-content's
 * ceiling is the PDF cap because it is the only bucket that takes PDFs and
 * Storage has a single limit per bucket; images there are still held to
 * MAX_IMAGE_BYTES by the client.
 */
export const BUCKET_UPLOAD_LIMITS: Record<StorageBucketId, BucketUploadLimits> = {
  avatars: { fileSizeLimit: MAX_IMAGE_BYTES, allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES },
  "gallery-photos": { fileSizeLimit: MAX_IMAGE_BYTES, allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES },
  "organization-content": {
    fileSizeLimit: MAX_PDF_BYTES,
    allowedMimeTypes: [...ALLOWED_IMAGE_MIME_TYPES, PDF_MIME_TYPE],
  },
};

/** Value for an `<input type="file" accept>` that offers only uploadable images. */
export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif";

type FileLike = Pick<File, "type" | "size">;

/** Null when the file may be uploaded as an image; otherwise a user-facing message. */
export function imageUploadError(file: FileLike): string | null {
  const type = (file.type || "").toLowerCase();
  if (!(ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(type)) {
    return "Please choose a JPG, PNG, WebP, GIF or AVIF image.";
  }
  if (file.size > MAX_IMAGE_BYTES) return "Image must be under 5 MB.";
  return null;
}

/** Null when the file may be uploaded as a PDF; otherwise a user-facing message. */
export function pdfUploadError(file: FileLike): string | null {
  if ((file.type || "").toLowerCase() !== PDF_MIME_TYPE) return "Please choose a PDF file.";
  if (file.size > MAX_PDF_BYTES) return "PDF must be under 10 MB.";
  return null;
}

/**
 * A clear message when Storage itself rejected an upload for its type or
 * size (a client that skipped the checks above, or a browser that reported a
 * different type than the file carries); null for any other failure, so the
 * caller keeps its own generic message.
 *
 * Storage reports these as 413 / 415, either as the HTTP status or as the
 * `statusCode` string in the body, depending on the server version.
 */
export function storageRejectionMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const e = error as { status?: unknown; statusCode?: unknown; message?: unknown; error?: unknown; code?: unknown };
  const codes = [e.status, e.statusCode].map((v) => String(v ?? ""));
  const text = [e.message, e.error, e.code].map((v) => String(v ?? "")).join(" ").toLowerCase();
  if (codes.includes("413") || /exceeded the maximum allowed size|payload too large|entitytoolarge/.test(text)) {
    return "That file is too large to upload.";
  }
  if (codes.includes("415") || /mime type|invalid_mime_type/.test(text)) {
    return "That file type isn't supported for upload.";
  }
  return null;
}

/** A client-side refusal whose message is safe to show as-is. */
export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadValidationError";
  }
}

/**
 * The message for a failed upload: our own validation message, else Storage's
 * type/size rejection, else the caller's generic fallback. Arbitrary error
 * text is never shown.
 */
export function uploadFailureMessage(error: unknown, fallback: string): string {
  if (error instanceof UploadValidationError) return error.message;
  return storageRejectionMessage(error) ?? fallback;
}
